<template>
  <div class="widget white_box">
    <div class="widget_header">
      <div class="location_tag">
        <h3 class="tw-text-neutral-900 tw-text-2xl">
          New Google Reviews <small>(3-5 Stars)</small>
        </h3>
        <div class="sub_heading">
          Location: {{ selectedLocation }}
        </div>
      </div>
      <DefaultButton
        v-if="!editing && !preview"
        size="small"
        as="a"
        :href="`/local/business-profile/reviews/${widget.location_id}/?filter_search_text=&filter_star_rating=5-4-3&filter_sentiment=&filter_response=`"
      >
        See more
      </DefaultButton>
      <div
        v-if="editing && !preview"
        class="edit_buttons tw-flex tw-gap-2"
      >
        <DefaultButton
          size="small"
          variant="primary-inverted"
          @click="$parent.edit(widget)"
        >
          Options
        </DefaultButton>

        <DefaultButton
          size="small"
          variant="danger"
          @click="$parent.remove(widget)"
        >
          Delete
        </DefaultButton>
      </div>
    </div>
    <div
      v-if="!loading && reviews.length > 0"
      :style="reviews.length <= 2 ? 'justify-content: space-around;' :''"
      class="review_items"
    >
      <div
        v-for="(review, index) in reviews"
        :key="index"
        class="review_item"
      >
        <div class="review_head">
          <div :class="`sentiment_label ${review.sentiment_text ? review.sentiment_text.toLowerCase() : ''}`" />
          <div class="review_name p_body font_bold">
            {{ review.reviewer_name }}
          </div>
          <div class="rating_count">
            <span
              v-for="star in parseInt(review.star_rating)"
              :key="star"
              class="rating_star"
            />
          </div>
          <div class="time">
            <i class="icon clock_item" />
            <span class="p_x_small">{{ daysAgo(review.review_date) }}</span>
          </div>
          <svg
            fill="none"
            height="18"
            viewBox="0 0 18 18"
            width="18"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.8125 9H15.1875"
              stroke="#37B2A8"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.125"
            />
            <path
              d="M10.125 3.9375L15.1875 9L10.125 14.0625"
              stroke="#37B2A8"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.125"
            />
          </svg>
        </div>
        <div class="review_body p_small">
          {{ review.comment }}
        </div>
      </div>
    </div>

    <div
      v-else
      class="loading_widget_data"
    >
      <div
        v-if="loading && !error"
        class="loading_widget_data"
      >
        <i class="fa fa-btn fa-spinner fa-spin" /> Loading Data
      </div>

      <div
        v-else-if="error"
        class="widget_error"
        v-html="error"
      />

      <div v-else>
        No new reviews in the past month
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {mapState} from 'vuex';
import axios from 'axios';
import _ from 'lodash';
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";

export default {
  components: {
    DefaultButton,
  },
  props: {
    widget: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      loading: true,
      reviews: [],
      error: null,
    }
  },
  computed: {
    ...mapState('dashboard', ['uuid', 'preview', 'gmbLocations']),
    selectedLocation() {
      return this.gmbLocations[this.widget.location_id];
    }
  },
  watch: {
    'widget.location_id': {
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal, oldVal)) this.fetchData();
      },
      deep: true
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.loading = true
      axios.post('/ajax/dashboard/get-widget-data', {
        type: 'newReviews3Plus',
        location_id: this.widget.location_id,
        uuid: this.uuid
      }).then(response => {
        this.reviews = response.data;
        this.loading = false;
      }).catch(error => {
        this.error = error.response.data;
        this.loading = false;
      });
    },
    daysAgo(date) {
      let days = moment().diff(moment(date), 'days');
      return days + ' days ago';
    },
  },
}
</script>

<style scoped>
.review_items {
  display: flex;
  flex-direction: column;
  max-height: 309px;
  overflow-y: auto;
  flex-grow: 1;
}

.review_item {
  padding: 1.5rem 1.5rem 2rem;
  border: 1px solid #E8E5E2;
  border-radius: 0.75rem;
}

.review_item:not(:last-of-type) {
  margin-bottom: 1rem;
}

.review_head {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.sentiment_label {
  margin-right: .5rem;
  border-radius: 6px;
  width: 1.5rem;
  height: 1.5rem;
  background-repeat: no-repeat;
  background-position: center;
  background-color: #F7F5F2;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z' stroke='%23C7C4C1' stroke-width='1.33333' stroke-miterlimit='10'/%3E%3Cpath d='M5.75 7.5C6.16421 7.5 6.5 7.16421 6.5 6.75C6.5 6.33579 6.16421 6 5.75 6C5.33579 6 5 6.33579 5 6.75C5 7.16421 5.33579 7.5 5.75 7.5Z' fill='%23C7C4C1'/%3E%3Cpath d='M10.25 7.5C10.6642 7.5 11 7.16421 11 6.75C11 6.33579 10.6642 6 10.25 6C9.83579 6 9.5 6.33579 9.5 6.75C9.5 7.16421 9.83579 7.5 10.25 7.5Z' fill='%23C7C4C1'/%3E%3C/svg%3E%0A");
}

.sentiment_label.positive {
  background-color: #DFF5EE;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z' stroke='%2337B2A8' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M10.5986 9.49976C10.3351 9.95564 9.95619 10.3342 9.50006 10.5973C9.04394 10.8605 8.52661 10.999 8.00001 10.999C7.47342 10.999 6.95608 10.8605 6.49995 10.5974C6.04382 10.3342 5.66495 9.95568 5.40137 9.4998' stroke='%2337B2A8' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M5.75 7.5C6.16421 7.5 6.5 7.16421 6.5 6.75C6.5 6.33579 6.16421 6 5.75 6C5.33579 6 5 6.33579 5 6.75C5 7.16421 5.33579 7.5 5.75 7.5Z' fill='%2337B2A8'/%3E%3Cpath d='M10.25 7.5C10.6642 7.5 11 7.16421 11 6.75C11 6.33579 10.6642 6 10.25 6C9.83579 6 9.5 6.33579 9.5 6.75C9.5 7.16421 9.83579 7.5 10.25 7.5Z' fill='%2337B2A8'/%3E%3C/svg%3E%0A");
}

.sentiment_label.negative {
  background-color: #FFEEEA;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z' stroke='%23F34F4F' stroke-width='1.33333' stroke-miterlimit='10'/%3E%3Cpath d='M10.5986 11C10.3351 10.5441 9.95619 10.1656 9.50006 9.90244C9.04394 9.63928 8.52661 9.50074 8.00001 9.50073C7.47342 9.50073 6.95608 9.63926 6.49995 9.90241C6.04382 10.1656 5.66495 10.5441 5.40137 11' stroke='%23F34F4F' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M5.75 7.5C6.16421 7.5 6.5 7.16421 6.5 6.75C6.5 6.33579 6.16421 6 5.75 6C5.33579 6 5 6.33579 5 6.75C5 7.16421 5.33579 7.5 5.75 7.5Z' fill='%23F34F4F'/%3E%3Cpath d='M10.25 7.5C10.6642 7.5 11 7.16421 11 6.75C11 6.33579 10.6642 6 10.25 6C9.83579 6 9.5 6.33579 9.5 6.75C9.5 7.16421 9.83579 7.5 10.25 7.5Z' fill='%23F34F4F'/%3E%3C/svg%3E%0A");
}

.review_name {
  margin-right: auto;
}

.rating_count {
  margin-right: .5rem;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
}

.rating_count .rating_star {
  display: block;
  width: 1rem;
  height: 1rem;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.27569 11.9208L11.4279 13.9179C11.8308 14.1732 12.3311 13.7935 12.2115 13.3232L11.3008 9.74052C11.2752 9.64073 11.2782 9.53573 11.3096 9.4376C11.3409 9.33946 11.3994 9.25218 11.4781 9.18577L14.3049 6.83306C14.6763 6.52392 14.4846 5.90751 14.0074 5.87654L10.3159 5.63696C10.2165 5.62986 10.1211 5.59465 10.0409 5.53545C9.96069 5.47625 9.89896 5.39548 9.86289 5.30255L8.48612 1.83549C8.44869 1.73685 8.38215 1.65194 8.29532 1.59201C8.2085 1.53209 8.1055 1.5 8 1.5C7.89451 1.5 7.79151 1.53209 7.70468 1.59201C7.61786 1.65194 7.55131 1.73685 7.51389 1.83549L6.13712 5.30255C6.10104 5.39548 6.03932 5.47625 5.95912 5.53545C5.87892 5.59465 5.78355 5.62986 5.68412 5.63696L1.99263 5.87654C1.51544 5.90751 1.32373 6.52392 1.69515 6.83306L4.52186 9.18577C4.60063 9.25218 4.65907 9.33946 4.69044 9.4376C4.72181 9.53573 4.72485 9.64073 4.6992 9.74052L3.85459 13.063C3.71111 13.6274 4.31143 14.083 4.79495 13.7767L7.72431 11.9208C7.8067 11.8683 7.90234 11.8405 8 11.8405C8.09767 11.8405 8.19331 11.8683 8.27569 11.9208Z' fill='%23FFD673' stroke='%23FFD673' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
}

.time {
  margin-right: .5rem;
}

.review_body {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
